import { Archive, FileAudio, FileJson, Image, Package, Play, TestTube2 } from "lucide-react";
import type { AudioLabInstrument, AudioLabTestCase } from "../../audio-lab/audioLabRegistry";
import { getAudioLabTestCases } from "../../audio-lab/audioLabRegistry";
import type { AudioLabAssetKind } from "../../audio-lab/audioLabZip";
import { AudioLabDownloadButton } from "./AudioLabDownloadButton";

interface AudioLabInstrumentActions {
  preview: (testCase: AudioLabTestCase) => void;
  presetJson: (instrument: AudioLabInstrument) => void;
  featureIndexJson: (instrument: AudioLabInstrument) => void;
  instrumentZip: (instrument: AudioLabInstrument) => void;
  assetZip: (instrument: AudioLabInstrument, assetKind: AudioLabAssetKind) => void;
  wav: (testCase: AudioLabTestCase) => void;
  spectrogram: (testCase: AudioLabTestCase) => void;
  features: (testCase: AudioLabTestCase) => void;
  testBundle: (testCase: AudioLabTestCase) => void;
}

interface AudioLabInstrumentCardProps {
  instrument: AudioLabInstrument;
  busy: boolean;
  actions: AudioLabInstrumentActions;
}

export function AudioLabInstrumentCard({ instrument, busy, actions }: AudioLabInstrumentCardProps) {
  const testCases = getAudioLabTestCases(instrument.id);
  const firstTestCase = testCases[0];

  return (
    <article className="audio-lab-instrument-card">
      <header>
        <div>
          <p className="eyebrow">{instrument.family}</p>
          <h2>{instrument.name}</h2>
          {instrument.region ? <span>{instrument.region}</span> : null}
        </div>
        <div className="audio-lab-card-counts">
          <span>{instrument.notes.length} notes</span>
          <span>{instrument.articulations.length} articulations</span>
          <span>{testCases.length} tests</span>
        </div>
      </header>

      <div className="audio-lab-card-actions">
        {firstTestCase ? (
          <AudioLabDownloadButton
            label="Preview"
            icon={<Play size={17} aria-hidden="true" />}
            disabled={busy}
            variant="quiet"
            onClick={() => actions.preview(firstTestCase)}
          />
        ) : null}
        <AudioLabDownloadButton
          label="Download Instrument ZIP"
          icon={<Archive size={17} aria-hidden="true" />}
          disabled={busy}
          onClick={() => actions.instrumentZip(instrument)}
        />
        <AudioLabDownloadButton
          label="Download Preset JSON"
          icon={<FileJson size={17} aria-hidden="true" />}
          disabled={busy}
          onClick={() => actions.presetJson(instrument)}
        />
        <AudioLabDownloadButton
          label="Download Feature Index JSON"
          icon={<TestTube2 size={17} aria-hidden="true" />}
          disabled={busy}
          onClick={() => actions.featureIndexJson(instrument)}
        />
        <AudioLabDownloadButton
          label="Download All WAVs"
          icon={<FileAudio size={17} aria-hidden="true" />}
          disabled={busy}
          onClick={() => actions.assetZip(instrument, "wav")}
        />
        <AudioLabDownloadButton
          label="Download All Spectrogram PNGs"
          icon={<Image size={17} aria-hidden="true" />}
          disabled={busy}
          onClick={() => actions.assetZip(instrument, "spectrogram")}
        />
        <AudioLabDownloadButton
          label="Download All Feature JSONs"
          icon={<Package size={17} aria-hidden="true" />}
          disabled={busy}
          onClick={() => actions.assetZip(instrument, "features")}
        />
      </div>

      <section className="audio-lab-test-list" aria-label={`${instrument.name} note and articulation tests`}>
        <div className="audio-lab-test-list-heading">
          <span>Note</span>
          <span>Articulation</span>
          <span>Exports</span>
        </div>
        {testCases.map((testCase) => (
          <div className="audio-lab-test-row" key={testCase.filenameStem}>
            <strong>{testCase.note}</strong>
            <code>{testCase.articulation}</code>
            <div>
              <button type="button" disabled={busy} onClick={() => actions.preview(testCase)} aria-label={`Play ${testCase.filenameStem}`}>
                <Play size={15} aria-hidden="true" />
                <span>Play</span>
              </button>
              <button type="button" disabled={busy} onClick={() => actions.wav(testCase)} aria-label={`Download WAV ${testCase.filenameStem}`}>
                <FileAudio size={15} aria-hidden="true" />
                <span>WAV</span>
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => actions.spectrogram(testCase)}
                aria-label={`Download spectrogram PNG ${testCase.filenameStem}`}
              >
                <Image size={15} aria-hidden="true" />
                <span>Spectrogram PNG</span>
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => actions.features(testCase)}
                aria-label={`Download feature JSON ${testCase.filenameStem}`}
              >
                <FileJson size={15} aria-hidden="true" />
                <span>Feature JSON</span>
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => actions.testBundle(testCase)}
                aria-label={`Download test bundle ZIP ${testCase.filenameStem}`}
              >
                <Archive size={15} aria-hidden="true" />
                <span>Bundle ZIP</span>
              </button>
            </div>
          </div>
        ))}
      </section>
    </article>
  );
}
